import { useState, useEffect } from 'react'
import { MessageSquare, Settings, Code, Download } from 'lucide-react'

interface WidgetConfig {
  chatId: string
  theme: {
    primaryColor: string
    backgroundColor: string
    textColor: string
  }
  features: {
    enableFileUpload: boolean
    enableWebSockets: boolean
    enableAnalytics: boolean
    enablePushNotifications: boolean
  }
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
  size: 'small' | 'medium' | 'large'
}

export default function App() {
  const [config, setConfig] = useState<WidgetConfig>({
    chatId: '',
    theme: {
      primaryColor: '#3b82f6',
      backgroundColor: '#ffffff',
      textColor: '#000000'
    },
    features: {
      enableFileUpload: true,
      enableWebSockets: false,
      enableAnalytics: false,
      enablePushNotifications: false
    },
    position: 'bottom-right',
    size: 'medium'
  })

  const [generatedCode, setGeneratedCode] = useState('')
  const [deployUrl, setDeployUrl] = useState('')

  useEffect(() => {
    // Get chatId from URL params
    const urlParams = new URLSearchParams(window.location.search)
    const chatId = urlParams.get('chatId')
    if (chatId) {
      setConfig(prev => ({ ...prev, chatId }))
    }
  }, [])

  const generateWidgetCode = () => {
    const widgetCode = `
<!-- Chat Widget Script -->
<script>
window.adiWidgetConfig = {
  chatId: '${config.chatId}',
  theme: ${JSON.stringify(config.theme)},
  features: ${JSON.stringify(config.features)},
  position: '${config.position}',
  size: '${config.size}',
  apiUrl: 'https://chat-platform-api.rckflr.workers.dev/api/v1'
};

(function() {
  var script = document.createElement('script');
  script.src = '${deployUrl}/widget.js';
  script.async = true;
  document.head.appendChild(script);
})();
</script>
    `.trim()

    setGeneratedCode(widgetCode)
  }

  const deployWidget = async () => {
    try {
      // This would typically call the API to deploy the widget
      const mockDeployUrl = `https://${config.chatId}.chat-widget.pages.dev`
      setDeployUrl(mockDeployUrl)
      generateWidgetCode()
    } catch (error) {
      console.error('Error deploying widget:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <MessageSquare className="mx-auto h-12 w-12 text-blue-600 mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">Chat Widget Deployer</h1>
          <p className="text-gray-600 mt-2">Configure and deploy your chat widget</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Configuration Panel */}
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                Widget Configuration
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chat ID
                  </label>
                  <input
                    type="text"
                    value={config.chatId}
                    onChange={(e) => setConfig({ ...config, chatId: e.target.value })}
                    className="input-field"
                    placeholder="Enter chat ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position
                  </label>
                  <select
                    value={config.position}
                    onChange={(e) => setConfig({ ...config, position: e.target.value as any })}
                    className="input-field"
                  >
                    <option value="bottom-right">Bottom Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="top-right">Top Right</option>
                    <option value="top-left">Top Left</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Size
                  </label>
                  <select
                    value={config.size}
                    onChange={(e) => setConfig({ ...config, size: e.target.value as any })}
                    className="input-field"
                  >
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Theme</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Primary Color
                  </label>
                  <input
                    type="color"
                    value={config.theme.primaryColor}
                    onChange={(e) => setConfig({
                      ...config,
                      theme: { ...config.theme, primaryColor: e.target.value }
                    })}
                    className="h-10 w-full rounded border border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Background Color
                  </label>
                  <input
                    type="color"
                    value={config.theme.backgroundColor}
                    onChange={(e) => setConfig({
                      ...config,
                      theme: { ...config.theme, backgroundColor: e.target.value }
                    })}
                    className="h-10 w-full rounded border border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Text Color
                  </label>
                  <input
                    type="color"
                    value={config.theme.textColor}
                    onChange={(e) => setConfig({
                      ...config,
                      theme: { ...config.theme, textColor: e.target.value }
                    })}
                    className="h-10 w-full rounded border border-gray-300"
                  />
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Features</h3>
              <div className="space-y-3">
                {Object.entries(config.features).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </label>
                    </div>
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => setConfig({
                        ...config,
                        features: { ...config.features, [key]: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={deployWidget}
              className="btn-primary w-full py-3"
            >
              Deploy Widget
            </button>
          </div>

          {/* Preview and Code Panel */}
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Code className="h-5 w-5 mr-2" />
                Generated Code
              </h2>
              
              {generatedCode ? (
                <div className="space-y-4">
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm">{generatedCode}</pre>
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(generatedCode)}
                    className="btn-secondary w-full flex items-center justify-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Copy Code
                  </button>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Code className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>Configure your widget and click "Deploy Widget" to generate the code</p>
                </div>
              )}
            </div>

            {deployUrl && (
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Widget URL</h3>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <code className="text-sm text-gray-700 break-all">{deployUrl}</code>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  This URL will be available once the widget is deployed
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
} 